/**
 * MentionInput — wraps an `<input>` or `<textarea>` and adds @mention
 * autocomplete. Storage is the raw string: when the user picks a match,
 * the active `@query` substring is replaced by `[@Label](kind:id)`.
 *
 * When no mention providers are registered, we fall back to a plain
 * input with no dropdown behavior — `@` is just text.
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { formatMention } from '../parse';
import { hasAnyProvider } from '../registry';
import { useMentionAutocomplete } from '../hooks/useMentionAutocomplete';
import { MentionDropdown } from './MentionDropdown';

type NativeElement = HTMLInputElement | HTMLTextAreaElement;

export interface MentionInputProps {
  as?: 'input' | 'textarea';
  value: string;
  onChange: (next: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<NativeElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  rows?: number;
  className?: string;
  inputClassName?: string;
  'aria-label'?: string;
}

export const MentionInput = forwardRef<NativeElement, MentionInputProps>(
  function MentionInput(
    {
      as = 'input',
      value,
      onChange,
      onKeyDown,
      placeholder,
      disabled,
      autoFocus,
      rows,
      className,
      inputClassName,
      ...ariaProps
    },
    ref,
  ) {
    const innerRef = useRef<NativeElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current as NativeElement, []);

    const [caret, setCaret] = useState(value.length);
    const { active, matches, loading } = useMentionAutocomplete(value, caret);
    const [activeIndex, setActiveIndex] = useState(0);

    // Reset highlighted option whenever the match list identity changes.
    useEffect(() => {
      setActiveIndex(0);
    }, [matches]);

    const hasProviders = hasAnyProvider();
    const showDropdown = hasProviders && active !== null;

    const insertMatch = useCallback(
      (matchIndex: number) => {
        if (!active) return;
        const entry = matches[matchIndex];
        if (!entry) return;
        // Notify the provider so it can e.g. auto-enable the subject.
        try {
          entry.provider.onInsert?.(entry.match);
        } catch {
          // Provider side-effects shouldn't break insertion.
        }
        const token = formatMention(entry.provider.kind, entry.match.id, entry.match.label);
        const before = value.slice(0, active.start);
        const after = value.slice(active.end);
        const insert = `${token} `;
        const nextValue = `${before}${insert}${after}`;
        onChange(nextValue);
        // Restore caret after the inserted token (+1 for the trailing space).
        const newCaret = active.start + insert.length;
        requestAnimationFrame(() => {
          const el = innerRef.current;
          if (!el) return;
          el.focus();
          try {
            el.setSelectionRange(newCaret, newCaret);
          } catch {
            // input type may not support selection range — ignore.
          }
          setCaret(newCaret);
        });
      },
      [active, matches, onChange, value],
    );

    const handleSelectionChange = useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      const pos = el.selectionStart ?? value.length;
      setCaret(pos);
    }, [value.length]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<NativeElement>) => {
        onChange(e.target.value);
        const pos = e.target.selectionStart ?? e.target.value.length;
        setCaret(pos);
      },
      [onChange],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<NativeElement>) => {
        if (showDropdown && matches.length > 0) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % matches.length);
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (i - 1 + matches.length) % matches.length);
            return;
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            insertMatch(activeIndex);
            return;
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            // Move caret one forward so detectActiveQuery no longer matches.
            const el = innerRef.current;
            if (el) {
              const end = value.length;
              setCaret(end);
              try {
                el.setSelectionRange(end, end);
              } catch {
                // ignore
              }
            }
            return;
          }
        }
        onKeyDown?.(e);
      },
      [showDropdown, matches, activeIndex, insertMatch, onKeyDown, value.length],
    );

    const commonProps = {
      ref: (el: NativeElement | null) => {
        innerRef.current = el;
      },
      value,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onKeyUp: handleSelectionChange,
      onClick: handleSelectionChange,
      onSelect: handleSelectionChange,
      placeholder,
      disabled,
      autoFocus,
      className: inputClassName,
      ...ariaProps,
    };

    return (
      <div className={`mention-input${className ? ` ${className}` : ''}`}>
        {as === 'textarea' ? (
          <textarea
            {...(commonProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            rows={rows}
          />
        ) : (
          <input
            {...(commonProps as React.InputHTMLAttributes<HTMLInputElement>)}
            type="text"
          />
        )}
        {showDropdown && (
          <MentionDropdown
            matches={matches}
            activeIndex={activeIndex}
            loading={loading}
            onSelect={insertMatch}
            onHoverIndex={setActiveIndex}
          />
        )}
      </div>
    );
  },
);
